import { useMemo, useState } from "react";
import AppHeader from "@/components/AppHeader";
import Main from "@/components/Main";
import WorkedExample from "@/components/WorkedExample";
import Seo from "@/components/Seo";
import {
  Zap,
  Battery,
  Shuffle,
  Check,
  X as XIcon,
  Lightbulb,
  RefreshCw,
  ArrowRight,
} from "@/lib/icons";
import {
  ECHEM_CATEGORY_LABELS,
  ECHEM_PROBLEMS,
  STANDARD_POTENTIALS,
  type EChemCategory,
  type EChemDifficulty,
  type MultipleChoiceProblem,
} from "@/data/electrochemistry";

const ALL_CATEGORIES = Object.keys(ECHEM_CATEGORY_LABELS) as EChemCategory[];
const ALL_DIFFICULTIES: EChemDifficulty[] = ["easy", "medium", "hard"];

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

interface AttemptState {
  problemId: string;
  selected: number | null;
  graded: boolean;
}

export default function Electrochemistry() {
  const [enabledCategories, setEnabledCategories] = useState<Set<EChemCategory>>(
    new Set(ALL_CATEGORIES),
  );
  const [difficulty, setDifficulty] = useState<EChemDifficulty | "all">("all");

  const pool = useMemo(
    () =>
      ECHEM_PROBLEMS.filter(
        (p) =>
          enabledCategories.has(p.category) &&
          (difficulty === "all" || p.difficulty === difficulty),
      ),
    [enabledCategories, difficulty],
  );

  const [current, setCurrent] = useState<MultipleChoiceProblem | null>(
    () => ECHEM_PROBLEMS[0] ?? null,
  );
  const [attempt, setAttempt] = useState<AttemptState>({
    problemId: ECHEM_PROBLEMS[0]?.id ?? "",
    selected: null,
    graded: false,
  });
  const [stats, setStats] = useState({ attempted: 0, correct: 0 });

  const next = () => {
    if (pool.length === 0) return;
    let p = pickRandom(pool);
    if (pool.length > 1 && current && p.id === current.id) {
      p = pickRandom(pool.filter((q) => q.id !== current.id));
    }
    setCurrent(p);
    setAttempt({ problemId: p.id, selected: null, graded: false });
  };

  const submit = () => {
    if (!current || attempt.selected === null) return;
    const ok = attempt.selected === current.correct;
    setAttempt((a) => ({ ...a, graded: true }));
    setStats((s) => ({
      attempted: s.attempted + 1,
      correct: s.correct + (ok ? 1 : 0),
    }));
  };

  const toggleCategory = (c: EChemCategory) => {
    setEnabledCategories((prev) => {
      const next = new Set(prev);
      if (next.has(c)) {
        if (next.size > 1) next.delete(c);
      } else next.add(c);
      return next;
    });
  };

  const accuracy =
    stats.attempted === 0
      ? 0
      : Math.round((stats.correct / stats.attempted) * 100);

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Electrochemistry Lessons — ElementPad" description="Galvanic cells, electrolysis, reduction potentials, and the Nernst equation explained with interactive visuals." path="/learn/electrochemistry" />
      <AppHeader />
      <Main className="max-w-5xl mx-auto px-4 py-8 space-y-10">
        {/* Header */}
        <header className="space-y-2">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-primary">
            <Zap size={14} aria-hidden="true" />
            Conceptual unit
          </div>
          <h1 className="text-3xl font-bold text-foreground">Electrochemistry</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Redox reactions, galvanic (voltaic) cells, and electrolysis. The
            chemistry behind every battery, fuel cell, and corroding nail.
          </p>
        </header>

        {/* ── Lesson ──────────────────────────────────────────────────────── */}
        <section
          aria-labelledby="lesson-heading"
          className="rounded-xl border border-border bg-tile-bg p-6 space-y-6"
        >
          <div className="flex items-center gap-2">
            <Lightbulb size={16} className="text-primary" aria-hidden="true" />
            <h2
              id="lesson-heading"
              className="text-lg font-semibold text-foreground"
            >
              The four moves of redox
            </h2>
          </div>

          <ol className="grid sm:grid-cols-2 gap-3 text-sm">
            {[
              {
                t: "1. Assign oxidation numbers",
                d: "Element = 0. Group I = +1, II = +2. O = −2 (peroxides −1). H = +1 (hydrides −1). Sum = compound charge.",
              },
              {
                t: "2. Spot the change",
                d: "Whoever gains electrons (ox# decreases) is REDUCED. Whoever loses electrons (ox# increases) is OXIDISED.",
              },
              {
                t: "3. Name the agents",
                d: "The oxidising agent is itself reduced. The reducing agent is itself oxidised. Confusing on purpose — practise it.",
              },
              {
                t: "4. Use LEO the lion goes GER",
                d: "Loss of Electrons = Oxidation. Gain of Electrons = Reduction. Or: OIL RIG — Oxidation Is Loss, Reduction Is Gain.",
              },
            ].map((s) => (
              <li
                key={s.t}
                className="rounded-lg border border-border bg-background/40 p-3"
              >
                <p className="font-semibold text-foreground text-sm">{s.t}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.d}</p>
              </li>
            ))}
          </ol>

          {/* Galvanic vs Electrolytic */}
          <div className="grid md:grid-cols-2 gap-3">
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Battery size={14} className="text-primary" aria-hidden="true" />
                <h3 className="text-sm font-semibold text-foreground">
                  Galvanic (voltaic) cell
                </h3>
              </div>
              <p className="text-xs text-muted-foreground">
                Spontaneous redox produces electricity. <strong>E°cell &gt; 0</strong>,{" "}
                <strong>ΔG° &lt; 0</strong>.
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                <li>Anode = oxidation = <strong>negative</strong> terminal</li>
                <li>Cathode = reduction = <strong>positive</strong> terminal</li>
                <li>Electrons flow anode → cathode through the wire</li>
                <li>Salt bridge balances charge with ion flow</li>
              </ul>
              <p className="font-mono text-xs text-foreground pt-1 border-t border-border">
                E°cell = E°cathode − E°anode
              </p>
            </div>
            <div className="rounded-lg border border-border bg-background/40 p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Zap size={14} className="text-primary" aria-hidden="true" />
                <h3 className="text-sm font-semibold text-foreground">
                  Electrolytic cell
                </h3>
              </div>
              <p className="text-xs text-muted-foreground">
                External power forces a non-spontaneous reaction. Used for
                electroplating, refining, splitting water.
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                <li>Anode = oxidation = <strong>positive</strong> terminal</li>
                <li>Cathode = reduction = <strong>negative</strong> terminal</li>
                <li>Polarity is opposite to a galvanic cell</li>
                <li>Charge passed: <span className="font-mono">Q = n·F</span> (F = 96,485 C/mol e⁻)</li>
              </ul>
            </div>
          </div>

          <WorkedExample
            title="EMF of a Zn|Cu galvanic cell"
            problem="Calculate E°cell for Zn(s) | Zn²⁺(aq) ‖ Cu²⁺(aq) | Cu(s). Given E°(Zn²⁺/Zn) = −0.76 V and E°(Cu²⁺/Cu) = +0.34 V."
            steps={[
              {
                label: "Step 1: Identify cathode and anode",
                explain:
                  "The half-cell with the more positive E° is reduced (cathode). Cu²⁺/Cu (+0.34) > Zn²⁺/Zn (−0.76), so Cu is the cathode.",
              },
              {
                label: "Step 2: Apply E°cell = E°cathode − E°anode",
                explain: "Always 'cathode minus anode' using the tabulated REDUCTION potentials.",
                formula: "E°cell = (+0.34) − (−0.76)",
              },
              {
                label: "Step 3: Compute",
                explain: "Subtracting a negative flips the sign — students lose marks here every year.",
                formula: "E°cell = +1.10 V",
              },
              {
                label: "Step 4: Interpret",
                explain: "E°cell > 0 → spontaneous → ΔG° < 0. The cell can do electrical work.",
              },
            ]}
            answer="E°cell = +1.10 V (spontaneous galvanic cell)"
          />

          {/* Standard reduction potentials reference table */}
          <details className="rounded-lg border border-border bg-background/40">
            <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-foreground hover:bg-muted/40 transition-colors">
              Standard reduction potentials (25 °C, 1 M)
            </summary>
            <div className="px-4 pb-4 max-h-72 overflow-y-auto">
              <table className="w-full text-xs font-mono">
                <thead className="text-muted-foreground uppercase tracking-wider text-[10px]">
                  <tr className="border-b border-border">
                    <th className="text-left py-2">Half-reaction</th>
                    <th className="text-right py-2">E° (V)</th>
                  </tr>
                </thead>
                <tbody>
                  {STANDARD_POTENTIALS.map((row) => (
                    <tr key={row.half} className="border-b border-border/50 last:border-0">
                      <td className="py-1.5 text-foreground">{row.half}</td>
                      <td
                        className={`py-1.5 text-right ${
                          row.E > 0 ? "text-primary" : "text-muted-foreground"
                        }`}
                      >
                        {row.E > 0 ? `+${row.E.toFixed(2)}` : row.E.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[10px] text-muted-foreground mt-3">
                More positive E° = stronger oxidising agent (eager to be reduced).
                More negative E° = stronger reducing agent (eager to lose electrons).
              </p>
            </div>
          </details>
        </section>

        {/* ── Drill ───────────────────────────────────────────────────────── */}
        <section aria-labelledby="drill-heading" className="space-y-4">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h2
                id="drill-heading"
                className="text-lg font-semibold text-foreground"
              >
                Practice drill
              </h2>
              <p className="text-xs text-muted-foreground">
                Multiple choice, instant feedback with reasoning.
              </p>
            </div>
            <div
              className="text-xs font-mono text-muted-foreground"
              aria-live="polite"
            >
              <span className="text-foreground font-semibold">{stats.correct}</span>
              /{stats.attempted} correct
              {stats.attempted > 0 && (
                <span className="ml-2">({accuracy}% accuracy)</span>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="rounded-xl border border-border bg-tile-bg p-4 space-y-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Difficulty
              </p>
              <div className="flex flex-wrap gap-2">
                {(["all", ...ALL_DIFFICULTIES] as const).map((d) => (
                  <button
                    key={d}
                    onClick={() => setDifficulty(d)}
                    className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors ${
                      difficulty === d
                        ? "bg-foreground text-background"
                        : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Categories
              </p>
              <div className="flex flex-wrap gap-2">
                {ALL_CATEGORIES.map((c) => {
                  const on = enabledCategories.has(c);
                  return (
                    <button
                      key={c}
                      onClick={() => toggleCategory(c)}
                      aria-pressed={on}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                        on
                          ? "bg-primary/10 border-primary/40 text-primary"
                          : "bg-muted border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {ECHEM_CATEGORY_LABELS[c]}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Drill card */}
          {current ? (
            <article className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-primary px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20">
                  {ECHEM_CATEGORY_LABELS[current.category]} · {current.difficulty}
                </span>
                <button
                  onClick={next}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                >
                  <Shuffle size={12} aria-hidden="true" />
                  New problem
                </button>
              </div>

              <p className="text-base text-foreground leading-relaxed">
                {current.prompt}
              </p>

              {current.formula && (
                <div className="rounded-lg border border-border bg-background/40 p-3 font-mono text-sm text-foreground">
                  {current.formula}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-2">
                {current.options.map((opt, i) => {
                  const isSelected = attempt.selected === i;
                  const isCorrect = i === current.correct;
                  let cls =
                    "border-border bg-background/40 hover:bg-muted/60 hover:border-muted-foreground/40";
                  if (attempt.graded) {
                    if (isCorrect)
                      cls =
                        "border-emerald-500/40 bg-emerald-500/10 text-emerald-400";
                    else if (isSelected)
                      cls =
                        "border-destructive/40 bg-destructive/10 text-destructive";
                    else cls = "border-border bg-background/40 opacity-60";
                  } else if (isSelected) {
                    cls = "border-primary bg-primary/10";
                  }
                  return (
                    <button
                      key={i}
                      disabled={attempt.graded}
                      onClick={() =>
                        setAttempt((a) => ({ ...a, selected: i }))
                      }
                      className={`text-left rounded-lg border p-3 text-sm transition-colors ${cls}`}
                    >
                      <span className="inline-flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">
                          {String.fromCharCode(65 + i)}.
                        </span>
                        <span className="text-foreground">{opt}</span>
                        {attempt.graded && isCorrect && (
                          <Check
                            size={14}
                            className="text-emerald-400 ml-auto"
                            aria-hidden="true"
                          />
                        )}
                        {attempt.graded && isSelected && !isCorrect && (
                          <XIcon
                            size={14}
                            className="text-destructive ml-auto"
                            aria-hidden="true"
                          />
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-2">
                {!attempt.graded ? (
                  <button
                    onClick={submit}
                    disabled={attempt.selected === null}
                    className="h-10 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Check answer
                  </button>
                ) : (
                  <button
                    onClick={next}
                    className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                  >
                    Next problem
                    <ArrowRight size={14} aria-hidden="true" />
                  </button>
                )}
              </div>

              {attempt.graded && (
                <div
                  role="status"
                  className={`rounded-lg border p-3 text-sm ${
                    attempt.selected === current.correct
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : "border-destructive/30 bg-destructive/5"
                  }`}
                >
                  <p
                    className={`font-medium ${
                      attempt.selected === current.correct
                        ? "text-emerald-400"
                        : "text-destructive"
                    }`}
                  >
                    {attempt.selected === current.correct
                      ? "Correct."
                      : `Correct answer: ${String.fromCharCode(65 + current.correct)}. ${current.options[current.correct]}`}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {current.insight}
                  </p>
                </div>
              )}
            </article>
          ) : (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              No problems match your filters.
              <button
                onClick={() => {
                  setEnabledCategories(new Set(ALL_CATEGORIES));
                  setDifficulty("all");
                }}
                className="ml-2 inline-flex items-center gap-1 text-primary hover:underline"
              >
                <RefreshCw size={12} aria-hidden="true" />
                Reset filters
              </button>
            </div>
          )}
        </section>
      </Main>
    </div>
  );
}